import * as React from 'react';
import { cn } from '@/lib/utils';
import { HiveAccent } from '@/components/brand/hive-accent';

// DESIGN.md section 8, the app card: white, 1px border, 12px radius, the
// faintest shadow. Padding 20px; large analytics cards pass p-6.
// `tone`: good | attention | critical | info. The ground carries the meaning.
const TONE_CLS = { good: 'card-good', attention: 'card-attention', critical: 'card-critical', info: 'card-info' };
// A card that IS the control (a lead you tap, a link) lifts 2px with a softer
// shadow on hover (.cb-lift, App.css: transform and opacity only, still under
// reduced motion). An onClick alone is not enough: a dialog's card stops
// clicks with one. It needs a role of button or link, an href, a tabIndex or
// cursor-pointer; `interactive` forces it on or off.
const isInteractive = ({ interactive, onClick, role, href, tabIndex, className }) => {
  if (interactive != null) return !!interactive;
  if (href) return true;
  if (typeof onClick !== 'function') return false;
  return role === 'button' || role === 'link' || tabIndex != null || /(^|\s)cursor-pointer(\s|$)/.test(className || '');
};
// Attention is where the data needs working, so an attention-toned card
// carries the working hive - the bee filling the comb - as a watermark in
// its top-right corner. Behind the content, faded, inert.
const Card = React.forwardRef(({ className, tone, interactive, children, ...props }, ref) => {
  const lift = isInteractive({ interactive, className, ...props });
  return (
    <div ref={ref} className={cn('rounded-lg border border-[hsl(var(--card-border))] bg-card text-card-foreground', lift && 'cb-lift relative', tone && TONE_CLS[tone], tone === 'attention' && 'relative overflow-hidden', className)} {...props}>
      {tone === 'attention' && (
        <div className="pointer-events-none absolute -right-3 -top-4 w-28 opacity-45" aria-hidden="true"><HiveAccent className="w-full" /></div>
      )}
      {children}
    </div>
  );
});
Card.displayName = 'Card';

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  // The title band every section in the app starts with (see ui/panel.jsx).
  <div ref={ref} className={cn('mb-5 flex flex-col gap-1 rounded-t-lg border-b border-[hsl(var(--border-light))] px-5 py-3.5', className)} {...props} />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef(({ className, children, ...props }, ref) => (
  <h3 ref={ref} className={cn('text-[15px] font-semibold leading-snug tracking-[-0.005em] text-foreground', className)} {...props}>{children}</h3>
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-[13px] leading-relaxed text-muted-foreground', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
));
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
